import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { postingPill, LINK } from '../lib/hiring';
import { ShareCard, CopyButton } from './HiringShare'; // @@JOB_SHARE_v111@@

// @@JOBS_v86@@ Edit one job posting. Saving an OPEN posting updates the public
// page immediately (within the site's ~30s cache). Drafts are never public.
const S = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 },
  label: { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, color: 'var(--hq-muted)', textTransform: 'uppercase', letterSpacing: '.06em' },
  input: { width: '100%', boxSizing: 'border-box' },
  area: { width: '100%', boxSizing: 'border-box', height: 'auto', padding: '10px 12px', resize: 'vertical', lineHeight: 1.5 },
  body: { padding: 20, display: 'flex', flexDirection: 'column', gap: 16 },
  ghost: { width: 'auto', padding: '0 14px', marginTop: 0 },
};

function Field({ label, value, onChange, max, multiline, rows = 3, hint }) {
  return (
    <label style={S.label}>{label}
      {multiline
        ? <textarea className="hq-input" style={S.area} rows={rows} maxLength={max} value={value || ''} onChange={(e) => onChange(e.target.value)} />
        : <input className="hq-input" style={S.input} maxLength={max} value={value || ''} onChange={(e) => onChange(e.target.value)} />}
      {hint && <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>{hint}</span>}
    </label>
  );
}
function Lines({ label, value, onChange, rows, hint }) {
  return (
    <label style={S.label}>{label}
      <textarea className="hq-input" style={S.area} rows={rows} value={(value || []).join('\n')} onChange={(e) => onChange(e.target.value.split('\n'))} />
      <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>{hint || 'One per line.'}</span>
    </label>
  );
}

export default function HiringPosting() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [saved, setSaved] = useState(null);
  const [c, setC] = useState(null);
  const [slug, setSlug] = useState('');
  const [assets, setAssets] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState('');

  function apply(posting) { setP(posting); setC(posting.content); setSlug(posting.slug); setSaved(JSON.stringify([posting.slug, posting.content])); }
  async function load() {
    const res = await apiFetch('/api/jobs/postings/' + id);
    const d = await res.json().catch(() => ({}));
    if (res.ok) apply(d.posting); else setMsg(res.status === 404 ? 'Posting not found.' : (d.error || 'Could not load.'));
    const ar = await apiFetch('/api/site/assets'); // images uploaded in HQ › Website
    if (ar.ok) { const ad = await ar.json(); setAssets(ad.assets || []); }
  }
  useEffect(() => { load(); }, [id]);

  const dirty = useMemo(() => c && saved !== JSON.stringify([slug, c]), [c, slug, saved]);
  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));

  async function save() {
    setMsg(''); setBusy('save');
    try {
      const res = await apiFetch('/api/jobs/postings/' + id, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ slug, content: c }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { apply(d.posting); setMsg(d.posting.status === 'open' ? 'Saved — the live page updates within ~30 seconds.' : 'Saved.'); return true; }
      setMsg(d.error || 'Could not save.'); return false;
    } finally { setBusy(''); }
  }
  async function setStatus(status) {
    if (dirty && !(await save())) return;
    const ask = { open: 'Open this posting? It will appear on xyzmerch.com/jobs and accept applications.', closed: 'Close this posting? The page will show “Position filled” and stop taking applications.', draft: 'Move back to draft? The public page will disappear.' }[status];
    if (!window.confirm(ask)) return;
    const res = await apiFetch('/api/jobs/postings/' + id + '/status', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }),
    });
    const d = await res.json().catch(() => ({}));
    if (res.ok) { apply(d.posting); setMsg(status === 'open' ? 'Live — applications will show up under Hiring.' : 'Status updated.'); } else setMsg(d.error || 'Could not change the status.');
  }
  async function preview() {
    if (dirty && !(await save())) return;
    const win = window.open('', '_blank');
    const res = await apiFetch('/api/jobs/postings/' + id + '/preview');
    if (!res.ok) { if (win) win.close(); setMsg('Could not build the preview.'); return; }
    const url = URL.createObjectURL(new Blob([await res.text()], { type: 'text/html' }));
    if (win) win.location.href = url; else window.location.href = url;
  }
  async function remove() {
    if (!window.confirm('Delete this posting? This cannot be undone.')) return;
    const res = await apiFetch('/api/jobs/postings/' + id, { method: 'DELETE' });
    const d = await res.json().catch(() => ({}));
    if (res.ok) navigate('/hiring'); else setMsg(d.error || 'Could not delete.');
  }

  if (!c) {
    return <div className="hq-page-content"><div className="hq-page-head"><h1>Posting</h1><p>{msg || 'Loading…'}</p></div><Link to="/hiring" style={LINK}>← Hiring</Link></div>;
  }

  return (
    <div className="hq-page-content">
      <Link to="/hiring" style={{ ...LINK, fontSize: 13 }}>← Hiring</Link>
      <div className="hq-page-head" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16, marginTop: 8 }}>
        <div>
          <h1>{c.title || 'Untitled'} <span style={{ ...postingPill(p.status), verticalAlign: 'middle', marginLeft: 8 }}>{p.status}</span></h1>
          <p>{p.status === 'draft' ? 'Draft — not visible on the site.' : <><a href={p.public_url} target="_blank" rel="noopener noreferrer" style={LINK}>{p.public_url.replace(/^https?:\/\//, '')}</a> <CopyButton text={p.public_url} label="Copy URL" style={{ marginLeft: 6, verticalAlign: 'middle' }} /></>}
            {p.applications > 0 && <> · <Link to="/hiring" style={LINK}>{p.applications} applicant{p.applications === 1 ? '' : 's'}</Link></>}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="hq-signout" style={S.ghost} onClick={preview}>Preview</button>
          <button type="button" className="hq-signout" style={S.ghost} disabled={!dirty || !!busy} onClick={save}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
          {p.status !== 'open' && <button type="button" className="hq-btn" onClick={() => setStatus('open')}>{p.status === 'closed' ? 'Reopen' : 'Open posting'}</button>}
          {p.status === 'open' && <button type="button" className="hq-btn" onClick={() => setStatus('closed')}>Close posting</button>}
        </div>
      </div>
      {msg && <p className="hq-msg" style={{ marginTop: -12, marginBottom: 16 }}>{msg}</p>}
      {p.status === 'open' && dirty && <p className="hq-msg" style={{ marginTop: -8, marginBottom: 16, color: '#e8c15a' }}>This posting is live — saving updates the public page.</p>}

      <ShareCard postingId={p.id} slug={p.slug} status={p.status} />{/* @@JOB_SHARE_v111@@ */}

      <div className="hq-panel" style={{ marginBottom: 16 }}>
        <div className="hq-panel-head"><h2>Basics</h2></div>
        <div style={S.body}>
          <div style={S.grid}>
            <Field label="Job title" value={c.title} max={80} onChange={(v) => set('title', v)} />
            <Field label="Page URL" value={slug} max={80} onChange={(v) => setSlug(v.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} hint={`xyzmerch.com/jobs/${slug}`} />
            <Field label="Location" value={c.location} max={80} onChange={(v) => set('location', v)} />
            <Field label="Type" value={c.employment_type} max={60} onChange={(v) => set('employment_type', v)} hint="e.g. Full-time · on-site" />
            <Field label="Pay (optional)" value={c.pay} max={80} onChange={(v) => set('pay', v)} hint="e.g. $18–22/hr" />
          </div>
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 16 }}>
        <div className="hq-panel-head"><h2>The pitch</h2></div>
        <div style={S.body}>
          <div style={S.grid}>
            <Field label="Headline (white)" value={c.headline_1} max={60} onChange={(v) => set('headline_1', v)} />
            <Field label="Headline (green)" value={c.headline_2} max={60} onChange={(v) => set('headline_2', v)} />
            <Field label="Tagline" value={c.tagline} max={100} onChange={(v) => set('tagline', v)} />
          </div>
          <Field label="Intro" multiline value={c.intro} max={600} onChange={(v) => set('intro', v)} />
          <Field label="Highlighted line" multiline rows={2} value={c.highlight} max={300} onChange={(v) => set('highlight', v)} />
          <div style={S.grid}>
            <Lines label="What you’ll actually be doing" rows={7} value={c.duties} onChange={(v) => set('duties', v)} />
            <Lines label="What we’re looking for" rows={7} value={c.requirements} onChange={(v) => set('requirements', v)} />
          </div>
          <Field label="Growth line" multiline rows={2} value={c.growth} max={300} onChange={(v) => set('growth', v)} />
          <div style={S.grid}>
            <label style={S.label}>Header photo
              <select className="hq-select" style={S.input} value={c.image || ''} onChange={(e) => set('image', e.target.value || null)}>
                <option value="default-pano">Warehouse panorama (built-in)</option>
                <option value="default-overhead">Warehouse overhead (built-in)</option>
                <option value="">No photo (plain dark)</option>
                {assets.map((a) => <option key={a.id} value={a.id}>{a.filename || a.id.slice(0, 8)}</option>)}
              </select>
              <span style={{ textTransform: 'none', letterSpacing: 0, fontSize: 12 }}>Upload more images in Website › Images.</span>
            </label>
          </div>
        </div>
      </div>

      <div className="hq-panel" style={{ marginBottom: 16 }}>
        <div className="hq-panel-head"><h2>Application questions</h2></div>
        <div style={S.body}>
          <Lines label="Questions (up to 5, all required)" rows={5} value={c.questions} onChange={(v) => set('questions', v)}
            hint="One per line. Applicants also give name, email, and optionally phone, city and a resume/LinkedIn link. Changing questions doesn’t alter answers already received." />
        </div>
      </div>

      {p.applications === 0 && (
        <button type="button" className="hq-signout" style={{ ...S.ghost, color: '#ff7a73' }} onClick={remove}>Delete posting</button>
      )}
    </div>
  );
}
